import React, { useState } from 'react';
import FlairCard from '../../../../../atomic/templates/FlairCard/FlairCard';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import HubElementIcon from '../../../components/HubElementIcon/HubElementIcon';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import {
  HubElement,
  HubElementContentType,
  useHubElements,
} from '../../../hooks/useHubElements';
import { useHubElementHandler } from '../../../hooks/useHubElementHandler';
import './CustomLinksWidget.css';

const i18Path = 'dashboard.customLinks';

const CustomLinksWidget: React.FC = () => {
  const t = useNamespacedTranslation(i18Path);
  const { dashboard, getGroupChildren, loading } = useHubElements();
  const onActivate = useHubElementHandler();

  if (loading || dashboard.length === 0) {
    return null;
  }

  return (
    <FlairCard cardTitle={t('title')}>
      <div className="d-flex flex-column gap-1">
        {dashboard.map((element) =>
          element.config.contentType === HubElementContentType.Group ? (
            <CustomLinkGroup
              key={element.Id}
              element={element}
              children={getGroupChildren(element.Id).filter(
                (c) => c.config.contentType !== HubElementContentType.Group,
              )}
              onActivate={onActivate}
            />
          ) : (
            <CustomLinkRow
              key={element.Id}
              element={element}
              onClick={onActivate}
            />
          ),
        )}
      </div>
    </FlairCard>
  );
};

type CustomLinkRowProps = {
  element: HubElement;
  onClick: (el: HubElement) => void;
  nested?: boolean;
};

const CustomLinkRow: React.FC<CustomLinkRowProps> = ({
  element,
  onClick,
  nested = false,
}) => {
  const isExternal =
    element.config.contentType === HubElementContentType.ExternalUrl;
  return (
    <button
      type="button"
      className={`custom-link-row${nested ? ' custom-link-row--nested' : ''}`}
      onClick={() => onClick(element)}>
      <HubElementIcon iconValue={element.config.icon} size="sm" />
      <span className="flex-grow-1 text-start">
        {element.flair__Menu_Item_Name__c || element.Name}
      </span>
      {isExternal && <FlairIcon icon="external-link-outline" size="sm" />}
    </button>
  );
};

type CustomLinkGroupProps = {
  element: HubElement;
  children: HubElement[];
  onActivate: (el: HubElement) => void;
};

const CustomLinkGroup: React.FC<CustomLinkGroupProps> = ({
  element,
  children,
  onActivate,
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <button
        type="button"
        className="custom-link-row"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}>
        <HubElementIcon iconValue={element.config.icon} size="sm" />
        <span className="flex-grow-1 text-start">
          {element.flair__Menu_Item_Name__c || element.Name}
        </span>
        <FlairIcon
          icon={expanded ? 'caret-up-outline' : 'caret-down-outline'}
          size="sm"
        />
      </button>
      {expanded &&
        children.map((child) => (
          <CustomLinkRow
            key={child.Id}
            element={child}
            onClick={onActivate}
            nested
          />
        ))}
    </>
  );
};

export default CustomLinksWidget;
